import { Controller } from "@hotwired/stimulus"

const PAGE_PARAM = "preview_page"
const ANSWER_NAME = /^survey_response\[answers\]\[([^\]]+)\]\[answer\]/
const REQUIRED_MESSAGE = "Please answer this question."
// The wording Survey::NumberBounds uses on the server, which re-checks every
// answer this validates here.
const NOT_A_NUMBER_MESSAGE = "Please enter a number."
const BELOW_MINIMUM_MESSAGE = (bound) => `Please enter a number that is ${bound} or more.`
const ABOVE_MAXIMUM_MESSAGE = (bound) => `Please enter a number that is ${bound} or less.`
// Survey::TextLength's wording. The form is novalidate, so the browser enforces
// none of the minlength/maxlength it is given — this does.
const CHARACTER_PHRASE = (count) => `${count} character${count === 1 ? "" : "s"}`
const TOO_SHORT_MESSAGE = (limit) => `Please enter at least ${CHARACTER_PHRASE(limit)}.`
const TOO_LONG_MESSAGE = (limit) => `Please enter at most ${CHARACTER_PHRASE(limit)}.`
// Survey::AnswerValidator's two contact patterns and their wording. The email
// and url input types are keyboard hints only under a novalidate form.
const EMAIL_MESSAGE = "Please enter a valid email address."
const LINK_MESSAGE = "Please enter a valid link."
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const LINK_PATTERN = /^(https?:\/\/)?[^\s/?#@]+\.[^\s/?#@]+([/?#]\S*)?$/i

// The survey runner, shared by the admin preview and both member take pages.
// The preview passes validate=false and a form that never submits; the take
// pages validate before advancing and post for real.
export default class extends Controller {
  static targets = ["page", "previousButton", "thankYou", "block", "form"]
  static values = {
    currentPage: { type: Number, default: 0 },
    editorUrl: String,
    rules: { type: Array, default: [] },
    validate: { type: Boolean, default: false },
    syncUrl: { type: Boolean, default: false },
    submitted: { type: Boolean, default: false }
  }

  connect() {
    // The server decides this on a take page the member has already answered;
    // resetting it here would hide the Thank You it just rendered.
    this.submitted = this.submittedValue
    this.currentPageValue = this.initialPageIndex()
    this.applyVisibility()
    this.revealNearestVisiblePage()
    this.render({ syncUrl: true })
  }

  answerChanged(event) {
    this.clearBlockError(event?.target?.closest("[data-survey-runner-target='block']"))
    this.applyVisibility()
    this.revealNearestVisiblePage()
    this.render()
  }

  next(event) {
    event.preventDefault()

    if (!this.validatePage(this.currentPageValue)) return

    const nextPage = this.nextVisiblePage(this.currentPageValue)
    if (nextPage === null) {
      this.submit(event)
      return
    }

    this.currentPageValue = nextPage
    this.submitted = false
    this.render({ syncUrl: true })
  }

  previous(event) {
    event.preventDefault()

    if (this.submitted) return

    const previousPage = this.previousVisiblePage(this.currentPageValue)
    if (previousPage === null) return

    this.currentPageValue = previousPage
    this.render({ syncUrl: true })
  }

  // The preview has no form action and reveals its Thank You client-side; a
  // take page lets the real submit through once the page validates.
  submit(event) {
    if (!this.validatePage(this.currentPageValue)) {
      event.preventDefault()
      return
    }

    if (this.postsForReal()) {
      // `next` routes here with a click when every later page is conditionally
      // hidden; only a real submit event reaches the form on its own.
      if (event.type !== "submit") {
        event.preventDefault()
        this.formTarget.requestSubmit()
        return
      }

      this.disableSubmitButtons()
      return
    }

    event.preventDefault()
    if (!this.hasThankYouTarget) return

    this.submitted = true
    this.render()
    this.thankYouTarget.querySelector("[tabindex]")?.focus({ preventScroll: true })
  }

  postsForReal() {
    const action = this.hasFormTarget ? this.formTarget.getAttribute("action") : null

    return Boolean(action) && action !== "#"
  }

  disableSubmitButtons() {
    // The disabled state is what the design system paints; there is no
    // .app-btn.is-loading treatment to reach for.
    this.element.querySelectorAll("button[type='submit']").forEach((button) => {
      button.disabled = true
    })
  }

  // ---- validation -------------------------------------------------------

  validatePage(pageIndex) {
    if (!this.validateValue) return true

    const page = this.pageTargets[pageIndex]
    if (!page) return true

    let firstInvalid = null

    page.querySelectorAll("[data-survey-runner-target='block']").forEach((wrapper) => {
      this.clearBlockError(wrapper)
      if (wrapper.hidden) return

      const message = this.blockError(wrapper)
      if (!message) return

      this.markBlockInvalid(wrapper, message)
      if (!firstInvalid) firstInvalid = wrapper
    })

    if (!firstInvalid) return true

    const focusable = firstInvalid.querySelector("input:not([type='hidden']), textarea, select, button")
    focusable?.focus({ preventScroll: false })

    return false
  }

  blockError(wrapper) {
    if (wrapper.dataset.surveyRunnerRequired === "true" && !this.blockAnswered(wrapper)) return REQUIRED_MESSAGE

    return this.numberError(wrapper) || this.lengthError(wrapper) || this.formatError(wrapper)
  }

  // An email or link answer has one shape it has to have, and the novalidate
  // form leaves the input's own type enforcing nothing.
  formatError(wrapper) {
    const email = wrapper.querySelector("input[type='email']")
    if (email && !this.matches(email, EMAIL_PATTERN)) return EMAIL_MESSAGE

    const link = wrapper.querySelector("input[type='url']")
    if (link && !this.matches(link, LINK_PATTERN)) return LINK_MESSAGE

    return null
  }

  matches(field, pattern) {
    const answer = field.value.trim()

    return answer === "" || pattern.test(answer)
  }

  // The character limits ride on the field as minlength/maxlength, which the
  // novalidate form leaves entirely to us.
  lengthError(wrapper) {
    const field = wrapper.querySelector("input[minlength], input[maxlength], textarea[minlength], textarea[maxlength]")
    if (!field) return null

    const answer = field.value
    if (answer.trim() === "") return null

    if (field.minLength > 0 && answer.length < field.minLength) return TOO_SHORT_MESSAGE(field.minLength)
    if (field.maxLength > 0 && answer.length > field.maxLength) return TOO_LONG_MESSAGE(field.maxLength)

    return null
  }

  // A number question carries the range it accepts on the field that submits it,
  // so an answer outside it is caught before the page is posted.
  numberError(wrapper) {
    const field = wrapper.querySelector("[data-survey-runner-number]")
    if (!field) return null

    const answer = field.value.trim()
    if (answer === "") return null

    const number = Number(answer)
    if (Number.isNaN(number)) return NOT_A_NUMBER_MESSAGE

    const minimum = field.dataset.surveyRunnerMin
    const maximum = field.dataset.surveyRunnerMax
    if (minimum !== undefined && number < Number(minimum)) return BELOW_MINIMUM_MESSAGE(minimum)
    if (maximum !== undefined && number > Number(maximum)) return ABOVE_MAXIMUM_MESSAGE(maximum)

    return null
  }

  blockAnswered(wrapper) {
    const fileInput = wrapper.querySelector("input[type='file']")
    if (fileInput) return fileInput.files.length > 0

    const fields = wrapper.querySelectorAll("input, textarea, select")

    return Array.from(fields).some((field) => {
      if (field.disabled) return false
      if (field.type === "radio" || field.type === "checkbox") return field.checked

      return field.value.toString().trim() !== ""
    })
  }

  markBlockInvalid(wrapper, message) {
    wrapper.classList.add("is-invalid")
    wrapper.setAttribute("aria-invalid", "true")

    const error = wrapper.querySelector("[data-survey-runner-target='blockError']")
    if (!error) return

    error.textContent = message || error.textContent.trim() || REQUIRED_MESSAGE
    error.hidden = false
  }

  clearBlockError(wrapper) {
    if (!wrapper) return

    wrapper.classList.remove("is-invalid")
    wrapper.removeAttribute("aria-invalid")

    const error = wrapper.querySelector("[data-survey-runner-target='blockError']")
    if (error) error.hidden = true
  }

  // ---- file answers -----------------------------------------------------

  browseFiles(event) {
    event.preventDefault()
    this.fileInputFor(event.target)?.click()
  }

  filesChanged(event) {
    this.renderFileList(event.target)
    this.clearBlockError(event.target.closest("[data-survey-runner-target='block']"))
  }

  dragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("is-dragover")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("is-dragover")
  }

  dropFiles(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("is-dragover")

    const input = this.fileInputFor(event.currentTarget)
    if (!input || input.disabled) return

    const transfer = new DataTransfer()
    const dropped = Array.from(event.dataTransfer.files)
    const files = input.multiple ? Array.from(input.files).concat(dropped) : dropped.slice(0, 1)
    files.forEach((file) => transfer.items.add(file))
    input.files = transfer.files

    this.renderFileList(input)
    this.clearBlockError(event.currentTarget.closest("[data-survey-runner-target='block']"))
  }

  removeFile(event) {
    event.preventDefault()

    const index = Number.parseInt(event.currentTarget.dataset.fileIndex, 10)
    const input = this.fileInputFor(event.currentTarget)
    if (!input || Number.isNaN(index)) return

    const transfer = new DataTransfer()
    Array.from(input.files).forEach((file, fileIndex) => {
      if (fileIndex !== index) transfer.items.add(file)
    })
    input.files = transfer.files

    this.renderFileList(input)
  }

  fileInputFor(element) {
    return element.closest(".survey-block-item--file-upload")?.querySelector("input[type='file']")
  }

  renderFileList(input) {
    const list = input.closest(".survey-block-item--file-upload")?.querySelector("[data-survey-runner-target='fileList']")
    if (!list) return

    list.replaceChildren()
    const files = Array.from(input.files)
    list.hidden = files.length === 0

    files.forEach((file, index) => {
      const row = this.buildFileRow(input, file, index)
      if (row) list.appendChild(row)
    })
  }

  buildFileRow(input, file, index) {
    const template = input.closest(".survey-block-item--file-upload")
      ?.querySelector("[data-survey-runner-target='fileRowTemplate']")
    if (!template) return null

    const row = template.content.firstElementChild.cloneNode(true)
    const name = row.querySelector("[data-file-row='name']")
    name.textContent = file.name
    name.title = file.name
    row.querySelector("[data-file-row='size']").textContent = this.formatSize(file.size)

    const remove = row.querySelector("[data-file-row='remove']")
    remove.dataset.fileIndex = String(index)
    remove.setAttribute("aria-label", `Remove ${file.name}`)

    return row
  }

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // ---- paging -----------------------------------------------------------

  render({ syncUrl = false } = {}) {
    const currentPage = Math.min(Math.max(this.currentPageValue, 0), Math.max(this.pageTargets.length - 1, 0))
    this.currentPageValue = currentPage

    this.pageTargets.forEach((page, index) => {
      const visible = !this.submitted && index === currentPage
      page.hidden = !visible
      page.setAttribute("aria-hidden", visible ? "false" : "true")
    })

    if (this.hasThankYouTarget) {
      this.thankYouTarget.hidden = !this.submitted
      this.thankYouTarget.setAttribute("aria-hidden", this.submitted ? "false" : "true")
    }

    if (this.hasPreviousButtonTarget) {
      this.previousButtonTarget.hidden = this.submitted || this.previousVisiblePage(currentPage) === null
    }

    if (syncUrl) this.syncUrl(currentPage)
  }

  applyVisibility() {
    const answers = this.currentAnswers()

    this.blockTargets.forEach((wrapper) => {
      const blockId = wrapper.dataset.surveyRunnerBlockId
      const rule = this.rulesValue.find((entry) => String(entry.blockId) === String(blockId))
      const visible = !rule || answers[String(rule.sourceId)] === String(rule.equals)

      this.setBlockVisible(wrapper, visible)
    })
  }

  setBlockVisible(wrapper, visible) {
    const wasHidden = wrapper.hidden
    wrapper.hidden = !visible

    wrapper.querySelectorAll("input, textarea, select, button").forEach((field) => {
      if (field.dataset.surveyRunnerKeepEnabled) return

      if (visible) {
        if (field.dataset.surveyRunnerConditionDisabled !== "true") return
        delete field.dataset.surveyRunnerConditionDisabled
        field.disabled = false
        return
      }

      if (field.disabled) return
      field.dataset.surveyRunnerConditionDisabled = "true"
      field.disabled = true
    })

    if (!visible && !wasHidden) {
      this.clearBlockFields(wrapper)
      this.clearBlockError(wrapper)
    }
  }

  clearBlockFields(wrapper) {
    wrapper.querySelectorAll("input, textarea, select").forEach((field) => {
      // A ranking's fields carry its options, not a choice — blanking them would
      // leave the question unanswerable if the condition brings it back.
      if (field.dataset.surveyRunnerPreserve !== undefined) return

      if (field.type === "radio" || field.type === "checkbox") {
        field.checked = false
      } else {
        field.value = ""
      }
    })

    this.clearButtonSelections(wrapper)
  }

  // The star and scale answers live in a hidden field, so clearing that field alone
  // would leave the buttons still looking chosen while nothing is submitted.
  clearButtonSelections(wrapper) {
    wrapper.querySelectorAll("[data-survey-star-rating-value], [data-survey-numeric-scale-value]").forEach((option) => {
      option.classList.remove("is-selected")
      option.setAttribute("aria-checked", "false")

      const icon = option.querySelector(".ph-star")
      if (!icon) return

      icon.classList.remove("ph-fill")
      icon.classList.add("ph-thin")
    })
  }

  currentAnswers() {
    const answers = {}

    this.element.querySelectorAll("[name]").forEach((field) => {
      const match = field.name.match(ANSWER_NAME)
      if (!match) return
      if (field.disabled) return
      if ((field.type === "radio" || field.type === "checkbox") && !field.checked) return

      answers[match[1]] = field.value
    })

    return answers
  }

  pageHasVisibleBlocks(pageIndex) {
    const page = this.pageTargets[pageIndex]
    if (!page) return false

    const blocks = page.querySelectorAll("[data-survey-runner-target='block']")
    if (blocks.length === 0) return true

    return Array.from(blocks).some((block) => !block.hidden)
  }

  nextVisiblePage(fromIndex) {
    for (let index = fromIndex + 1; index < this.pageTargets.length; index += 1) {
      if (this.pageHasVisibleBlocks(index)) return index
    }

    return null
  }

  previousVisiblePage(fromIndex) {
    for (let index = fromIndex - 1; index >= 0; index -= 1) {
      if (this.pageHasVisibleBlocks(index)) return index
    }

    return null
  }

  revealNearestVisiblePage() {
    if (this.pageHasVisibleBlocks(this.currentPageValue)) return

    const nearest = this.nearestVisiblePage(this.currentPageValue)
    if (nearest !== this.currentPageValue) this.currentPageValue = nearest
  }

  nearestVisiblePage(fromIndex) {
    if (this.pageHasVisibleBlocks(fromIndex)) return fromIndex

    const previous = this.previousVisiblePage(fromIndex)
    if (previous !== null) return previous

    const next = this.nextVisiblePage(fromIndex)
    if (next !== null) return next

    return fromIndex
  }

  initialPageIndex() {
    if (!this.syncUrlValue) return this.currentPageValue

    const pageNumber = Number.parseInt(new URL(window.location.href).searchParams.get(PAGE_PARAM), 10)

    if (Number.isNaN(pageNumber)) return this.currentPageValue

    return pageNumber - 1
  }

  syncUrl(currentPage) {
    if (!this.syncUrlValue) return

    const url = new URL(window.location.href)

    if (currentPage > 0) {
      url.searchParams.set(PAGE_PARAM, String(currentPage + 1))
    } else {
      url.searchParams.delete(PAGE_PARAM)
    }

    if (url.href !== window.location.href) {
      window.history.replaceState({}, "", url.toString())
    }
  }
}
